import { useState } from "react";
import { apiUrls, urls } from "../../actprep-ui/utils/urls";
import {
  Box,
  Button,
  Chip,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid as Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { formatConstantOptions } from "../../actprep-ui/utils/formatting";
import { outOfGameReasons } from "../../constants/students";
import api from "../../actprep-ui/utils/api";
import { showSuccessSnackbar } from "../../actprep-ui/utils/feedback";
import { useConfirm } from "material-ui-confirm";
import { PARENT_APP_ORIGIN, TESTING_APP_ORIGIN } from "../../constants/misc";

export default function ASStudentEditForm({ row, onEdit, onClose }) {
  const studentFields = [
    "show_memorization",
    "show_on_good_news",
    "allow_overtime_tests",
    "opted_out_of_game",
  ];
  const weekFields = [
    "out_of_game_reason",
    "in_the_game",
    "quiz_score",
    "is_hours_volunteer",
  ];
  const confirm = useConfirm();
  const [formData, setFormData] = useState({
    uuid: row.uuid,
    ...Object.fromEntries(studentFields.map((field) => [field, row[field]])),
    current_week: {
      uuid: row.current_week.uuid,
      ...Object.fromEntries(
        weekFields.map((field) => [field, row.current_week[field]])
      ),
    },
  });

  const handleUpdateStudentVal = (field, val) => {
    setFormData((curr) => {
      const next = { ...curr, [field]: val };
      if (field === "opted_out_of_game") {
        next.current_week = {
          ...curr.current_week,
          in_the_game: !val,
          out_of_game_reason: val
            ? outOfGameReasons.OPTED_OUT
            : curr.current_week.out_of_game_reason === outOfGameReasons.OPTED_OUT
              ? outOfGameReasons.NA
              : curr.current_week.out_of_game_reason,
        };
      }
      return next;
    });
  };
  const handleUpdateWeekVal = (field, val) => {
    setFormData((curr) => ({
      ...curr,
      current_week: { ...curr.current_week, [field]: val },
    }));
  };
  const handleSubmit = async (event) => {
    event.preventDefault();
    if (
      !formData.current_week.in_the_game &&
      formData.current_week.out_of_game_reason == "N/A"
    ) {
      await confirm({
        title: "Submission Failed",
        description:
          "You must specify 'Out of game reason' before removing the student from the game.",
        hideCancelButton: true,
      });
      return;
    }
    const response = await api.post(
      apiUrls.admin.accountabilitySheetStudent(),
      formData
    );
    if (response.status == 200) {
      showSuccessSnackbar("Student Updated!");
      onEdit();
    }
  };
  return (
    <Box component={"form"} onSubmit={handleSubmit}>
      <Stack direction={"row"} spacing={1} mb={1}>
        <Chip
          label="Student Dashboard"
          size="small"
          onClick={() =>
            window.open(
              TESTING_APP_ORIGIN + urls.students.impersonate(row.uuid),
              "_blank"
            )
          }
        />
        <Chip
          label="Parent Dashboard"
          size="small"
          onClick={() =>
            window.open(
              `${PARENT_APP_ORIGIN}/impersonate/${row.parent.uuid}/`,
              "_blank"
            )
          }
        />
      </Stack>
      <Grid container sx={{ width: "100%" }} spacing={2}>
        <Grid
          size={{
            xs: 12,
            sm: 6,
          }}
        >
          <Typography variant="h6" fontWeight={"bold"} mb={2}>
            This Week
          </Typography>
          <Stack gap={2}>
            <TextField
              label="Quiz Score"
              variant="standard"
              size="small"
              fullWidth
              value={formData.current_week.quiz_score}
              onChange={(event) =>
                handleUpdateWeekVal("quiz_score", event.target.value)
              }
              type="number"
            />
            <FormControl fullWidth variant="standard" size="small">
              <InputLabel>Out of game reason</InputLabel>
              <Select
                value={formData.current_week.out_of_game_reason}
                label="Out of game reason"
                required
                onChange={(event) =>
                  handleUpdateWeekVal("out_of_game_reason", event.target.value)
                }
              >
                {formatConstantOptions(outOfGameReasons).map((option, i) => (
                  <MenuItem key={i} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.current_week.in_the_game}
                  onChange={(event, checked) =>
                    handleUpdateWeekVal("in_the_game", checked)
                  }
                  color="secondary"
                />
              }
              label="In the game"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.current_week.is_hours_volunteer}
                  onChange={(event, checked) =>
                    handleUpdateWeekVal("is_hours_volunteer", checked)
                  }
                  color="secondary"
                />
              }
              label="Hours Volunteer"
            />
          </Stack>
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 6,
          }}
        >
          <Typography variant="h6" fontWeight={"bold"} mb={2}>
            Overall
          </Typography>
          <Stack gap={2}>
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.show_memorization}
                    onChange={(event, checked) =>
                      handleUpdateStudentVal("show_memorization", checked)
                    }
                    color="secondary"
                  />
                }
                label="Show memorization"
              />
              <FormHelperText>
                Affects the visibility of {'"'}Memorization{'"'} schedule items
                for the student
              </FormHelperText>
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.show_on_good_news}
                  onChange={(event, checked) =>
                    handleUpdateStudentVal("show_on_good_news", checked)
                  }
                  color="secondary"
                />
              }
              label="Show on good news"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.allow_overtime_tests}
                  onChange={(event, checked) =>
                    handleUpdateStudentVal("allow_overtime_tests", checked)
                  }
                  color="secondary"
                />
              }
              label="Allow overtime tests (accomodations)"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.opted_out_of_game}
                  onChange={(event, checked) =>
                    handleUpdateStudentVal("opted_out_of_game", checked)
                  }
                  color="secondary"
                />
              }
              label="Student opted out of the game"
            />
          </Stack>
        </Grid>
        <Grid size={12} display={"flex"} justifyContent={"flex-end"}>
          <Button sx={{ mr: 1 }} onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit">Save</Button>
        </Grid>
      </Grid>
    </Box>
  );
}
